import type { ReactNode } from "react";
import { Link, Navigate } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import PageHeader from "@/components/shared/PageHeader";
import { EmbeddedPageContext } from "@/components/shared/EmbeddedPageContext";

/** One tab in a SideTabsPage. Tabs sharing a `group` are listed under the same heading. */
export interface SideTab {
  id: string;
  label: string;
  group?: string;
  icon: LucideIcon;
  render: () => ReactNode;
}

interface SideTabsNavProps {
  tabs: SideTab[];
  activeId: string;
  basePath: string;
  ariaLabel: string;
}

const groupTabs = (tabs: SideTab[]) =>
  tabs.reduce<{ label?: string; tabs: SideTab[] }[]>((groups, tab) => {
    const existing = groups.find((group) => group.label === tab.group);
    if (existing) existing.tabs.push(tab);
    else groups.push({ label: tab.group, tabs: [tab] });
    return groups;
  }, []);

/**
 * Left-hand tabs. On desktop it is a sticky vertical list (grouped under headings when tabs
 * have a `group`); below the lg breakpoint it becomes a horizontally scrollable tab strip.
 */
export const SideTabsNav = ({ tabs, activeId, basePath, ariaLabel }: SideTabsNavProps) => (
  <nav
    aria-label={ariaLabel}
    className="scrollbar-none -mx-1 flex gap-1 overflow-x-auto px-1 pb-1 lg:sticky lg:top-24 lg:mx-0 lg:block lg:space-y-4 lg:self-start lg:overflow-visible lg:rounded-xl lg:border lg:border-border lg:bg-card lg:p-2 lg:pb-2 dark:lg:border-neutral-800"
  >
    {groupTabs(tabs).map((group, index) => (
      <div key={group.label ?? index} className="contents lg:block lg:space-y-0.5">
        {group.label && (
          <p className="hidden px-3 pb-1 pt-1.5 text-[11px] font-semibold text-muted-foreground lg:block">{group.label}</p>
        )}
        {group.tabs.map((tab) => {
          const Icon = tab.icon;
          const active = tab.id === activeId;
          return (
            <Link
              key={tab.id}
              to={`${basePath}/${tab.id}`}
              aria-current={active ? "page" : undefined}
              className={cn(
                "flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary/70 hover:text-foreground",
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {tab.label}
            </Link>
          );
        })}
      </div>
    ))}
  </nav>
);

interface SideTabsPageProps {
  title: string;
  description?: string;
  /** Route prefix, e.g. "/admin/reports"; each tab lives at `${basePath}/${tab.id}`. */
  basePath: string;
  tabs: SideTab[];
  /** Current tab id from the URL; unknown or missing ids redirect to the first tab. */
  tabId?: string;
}

/** Page shell: title, sticky left tab list, and the active tab's content. */
export const SideTabsPage = ({ title, description, basePath, tabs, tabId }: SideTabsPageProps) => {
  const active = tabs.find((tab) => tab.id === tabId);
  if (!active) return <Navigate to={`${basePath}/${tabs[0].id}`} replace />;

  return (
    <div className="space-y-5">
      <PageHeader title={title} description={description} />
      <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-6">
        <SideTabsNav tabs={tabs} activeId={active.id} basePath={basePath} ariaLabel={title} />
        {/* key resets each tab's own state when switching */}
        <div key={active.id} className="min-w-0">
          <EmbeddedPageContext.Provider value>{active.render()}</EmbeddedPageContext.Provider>
        </div>
      </div>
    </div>
  );
};
